import { Patient } from "@/components/PatientCard";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Bed } from "lucide-react";
import { usePatientTATTimer } from "@/hooks/usePatientTATTimer";
import { useMDCompletionTime } from "@/hooks/useMDCompletionTime";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface TentativeDischargeTableRowProps {
  patient: Patient;
  onClick: () => void;
}

export const TentativeDischargeTableRow = ({ patient, onClick }: TentativeDischargeTableRowProps) => {
  const tatTimer = usePatientTATTimer(patient.uuid || patient.id);
  const mdCompletionTime = useMDCompletionTime(patient.uuid || patient.id);
  
  // Derive initiated status from MD completion (source of truth)
  const isInitiated = !!mdCompletionTime;
  const isDischarged = patient.dischargeStatus === "discharged";
  const isNotInitiatedActive = !isDischarged && !isInitiated;

  const getTATColor = (status?: string) => {
    switch (status) {
      case 'green': return 'text-success';
      case 'yellow': return 'text-warning';
      case 'red': return 'text-destructive';
      case 'exceeded': return 'text-destructive font-bold';
      default: return 'text-muted-foreground';
    }
  };

  const getTATBorderClass = (status?: string) => {
    switch (status) {
      case 'green': return 'border-l-success';
      case 'yellow': return 'border-l-warning';
      case 'red': return 'border-l-destructive';
      case 'exceeded': return 'border-l-destructive';
      default: return 'border-l-border';
    }
  };

  const getTATBackgroundClass = (status?: string) => {
    switch (status) {
      case 'green': return 'bg-success/5';
      case 'yellow': return 'bg-warning/5';
      case 'red': return 'bg-destructive/5';
      case 'exceeded': return 'bg-destructive/10';
      default: return 'bg-background';
    }
  };

  const formatExpectedDischarge = (date?: string) => {
    if (!date) return '-';
    try {
      return format(new Date(date), "MMM dd, yyyy");
    } catch {
      return '-';
    }
  };

  return (
    <TableRow 
      className={cn(
        "cursor-pointer hover:bg-muted/50 transition-colors border-l-4",
        // Discharged: keep TAT-based styling
        isDischarged
          ? cn(getTATBorderClass(tatTimer?.status), getTATBackgroundClass(tatTimer?.status))
          // Not initiated (Awaiting MD): neutral / grey styling
          : isNotInitiatedActive
          ? "border-l-border bg-background"
          // Otherwise use TAT-based styling
          : cn(getTATBorderClass(tatTimer?.status), getTATBackgroundClass(tatTimer?.status))
      )} 
      onClick={onClick}
    >
      {/* Patient Info Card */}
      <TableCell className="p-2">
        <div className="bg-card rounded-lg shadow-sm border-l-4 border-l-primary p-3 space-y-0.5">
          <p className="text-sm font-semibold text-foreground leading-tight">{patient.name}</p>
          <p className="text-xs text-muted-foreground leading-tight">
            {patient.id} • {patient.age} YRS • {patient.gender}
          </p>
        </div>
      </TableCell>

      {/* Ward / Bed */}
      <TableCell className="px-4 align-top pt-4">
        <div className="flex items-center gap-1.5 text-sm text-foreground">
          <Bed className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          <div className="space-y-0.5">
            <p className="font-medium leading-tight">{patient.ward || 'N/A'}</p>
            <p className="text-xs text-muted-foreground leading-tight">
              Room {patient.room}, Bed {patient.bed}
            </p>
          </div>
        </div>
      </TableCell>

      {/* Doctor */}
      <TableCell className="px-4 align-top pt-4">
        <p className="text-sm font-medium text-foreground leading-tight">{patient.doctor || '-'}</p>
      </TableCell>

      {/* Expected Discharge */}
      <TableCell className="px-4 align-top pt-4">
        <p className="text-sm font-medium text-foreground leading-tight">
          {formatExpectedDischarge(patient.expectedDischarge)}
        </p>
      </TableCell>

      {/* TAT Status */}
      <TableCell className="px-4 align-top pt-4">
        {tatTimer ? (
          <div className="space-y-1">
            <p className={cn("text-sm font-semibold font-mono leading-tight", getTATColor(tatTimer.status))}>
              {tatTimer.countdown}
            </p>
            <p className="text-xs text-muted-foreground leading-tight truncate">
              {tatTimer.activityName}
            </p>
            <p className="text-xs text-muted-foreground leading-tight font-mono">
              Elapsed: {tatTimer.elapsedTime}
            </p>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        )}
      </TableCell>

      {/* Status Badges - Using MD completion as source of truth */}
      <TableCell className="px-4 align-top pt-4">
        <div className="flex flex-col gap-1.5">
          {isDischarged ? (
            <Badge variant="default" className="text-xs bg-primary hover:bg-primary/90">
              Discharged
            </Badge>
          ) : isInitiated ? (
            <Badge variant="default" className="text-xs bg-success hover:bg-success/90 text-success-foreground">
              Initiated
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">
              Not Initiated
            </Badge>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};